import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCheck,
  faCircleInfo,
  faMagnifyingGlass,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import {
  BaseRecipient,
  ReportPreview,
  ReportSettings,
  UpdatePayload,
} from "app/containers/WeeklyCheckInMonitor/types";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "app/shadcnComponents/ui/drawer";
import { Input } from "app/shadcnComponents/ui/input";
import { Button as ShadcnButton } from "app/shadcnComponents/ui/button";
import { ScrollArea } from "app/shadcnComponents/ui/scroll-area";
import { Separator } from "app/shadcnComponents/ui/separator";
import { Card, CardContent } from "app/shadcnComponents/ui/card";
import { Skeleton } from "app/shadcnComponents/ui/skeleton";
import { useAppSelector } from "utils/redux/hooks";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import Button from "app/storybookComponents/Button";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import RecipientRow from "./RecipientRow";
import NonRecipientRow from "./NonRecipientRow";
import SectionHeader from "./SectionHeader";
import SubSectionHeader from "./SubSectionHeader";
import JumpPills from "./JumpPills";
import {
  ActionType,
  NonRecipientUser,
  Sections,
  DRAWER_WIDTH,
  SECTION_DISPLAY_LIMIT,
} from "./types";

export interface ReportPreviewDrawerProps<T extends BaseRecipient> {
  isOpen: boolean;
  onClose: () => void;
  reportName: string;
  preview: ReportPreview<T> | undefined;
  settings: ReportSettings | undefined;
  isLoading: boolean;
  isSaving: boolean;
  updateSettings: (payload: UpdatePayload) => void;
  renderRecipientSubtitle: (recipient: T) => ReactNode;
  recipientRoleLabel: string;
  /**
   * Role-specific noun phrases used inside the section tooltips.
   * - `group`: used in fractional/percentage contexts, e.g. "people in their span of control".
   * - `member`: singular form used in "at least one ___" / "no ___" contexts, e.g. "person in their span of control".
   */
  coverageSubject: {
    group: string;
    member: string;
  };
  onEditSettings?: () => void;
  seniorManagerRecipientIds?: Set<number>;
}

export default function ReportPreviewDrawer<T extends BaseRecipient>({
  isOpen,
  onClose,
  reportName,
  preview,
  settings,
  isLoading,
  isSaving,
  updateSettings,
  renderRecipientSubtitle,
  recipientRoleLabel,
  coverageSubject,
  onEditSettings,
  seniorManagerRecipientIds,
}: Readonly<ReportPreviewDrawerProps<T>>) {
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const usersById = useAppSelector(selectAllCompanyUsersById);

  const currentMode = settings?.mode ?? null;
  const currentThreshold = settings?.thresholdPercentage ?? null;

  const getModeLabel = () => {
    if (!currentMode) return "—";
    if (currentMode === "all_who_qualify") return "All who qualify";
    if (currentMode === "specific_people") return "Specific people";
    return currentMode;
  };

  const modeLabel = getModeLabel();

  /* ─── Recipient IDs set ─── */

  const recipientIds = useMemo(() => {
    if (!preview?.recipients) return new Set<number>();
    return new Set(preview.recipients.map((r) => r.userAccountId));
  }, [preview]);

  /* ─── Search helper ─── */

  const matchesSearch = useCallback(
    (
      firstName: string,
      lastName: string,
      jobTitle?: string | null,
      emailAddress?: string | null
    ) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        `${firstName} ${lastName}`.toLowerCase().includes(q) ||
        (jobTitle?.toLowerCase().includes(q) ?? false) ||
        (emailAddress?.toLowerCase().includes(q) ?? false)
      );
    },
    [search]
  );

  /* ─── Mode-aware section grouping ─── */

  const isSeniorManagerRecipient = useCallback(
    (userAccountId: number) =>
      seniorManagerRecipientIds?.has(userAccountId) ?? false,
    [seniorManagerRecipientIds]
  );

  const seniorManagerRecipients = useMemo((): T[] => {
    if (!preview?.recipients || !seniorManagerRecipientIds?.size) return [];
    return preview.recipients.filter((r) => {
      if (!isSeniorManagerRecipient(r.userAccountId)) return false;
      const email = usersById?.[r.userAccountId]?.emailAddress;
      return matchesSearch(r.firstName, r.lastName, r.jobTitle, email);
    });
  }, [
    preview,
    seniorManagerRecipientIds,
    isSeniorManagerRecipient,
    matchesSearch,
    usersById,
  ]);

  const sections = useMemo((): Sections<T> | null => {
    if (!preview?.recipients) return null;

    const filtered = preview.recipients.filter((r) => {
      if (isSeniorManagerRecipient(r.userAccountId)) return false;
      const email = usersById?.[r.userAccountId]?.emailAddress;
      return matchesSearch(r.firstName, r.lastName, r.jobTitle, email);
    });

    if (currentMode === "specific_people") {
      return {
        mode: "specific_people",
        manuallyAdded: filtered.filter(
          (r) =>
            r.status === "qualified_by_rules" ||
            r.status === "included_override"
        ),
        qualified: filtered.filter((r) => r.status === "not_included"),
      };
    }

    return {
      mode: "all_who_qualify",
      manuallyAdded: filtered.filter((r) => r.status === "included_override"),
      eligibleByRules: filtered.filter(
        (r) => r.status === "qualified_by_rules"
      ),
      manuallyExcluded: filtered.filter((r) => r.status === "excluded"),
      belowThreshold: filtered
        .filter((r) => r.status === "below_threshold")
        .sort((a, b) => b.percentInvited - a.percentInvited),
    };
  }, [
    preview,
    currentMode,
    isSeniorManagerRecipient,
    matchesSearch,
    usersById,
  ]);

  /* ─── Added but won't receive ─── */

  const addedWontReceive = useMemo(() => {
    if (!settings?.includedUsers || !usersById) return [];
    const results: NonRecipientUser[] = [];
    for (const u of settings.includedUsers) {
      if (recipientIds.has(u.userAccountId)) continue;
      const user = usersById[u.userAccountId];
      if (!user) continue;
      if (
        !matchesSearch(
          user.firstName,
          user.lastName,
          user.jobTitle,
          user.emailAddress
        )
      )
        continue;
      results.push({
        userAccountId: user.userAccountId,
        firstName: user.firstName,
        lastName: user.lastName,
        jobTitle: user.jobTitle,
      });
    }
    return results;
  }, [settings, usersById, recipientIds, matchesSearch]);

  const addedWontReceiveIds = useMemo(
    () => new Set(addedWontReceive.map((u) => u.userAccountId)),
    [addedWontReceive]
  );

  /* ─── Non-recipient users ─── */

  const nonRecipientUsers = useMemo(() => {
    if (!usersById || !search) return [];
    const results: NonRecipientUser[] = [];
    for (const user of Object.values(usersById)) {
      if (recipientIds.has(user.userAccountId)) continue;
      if (addedWontReceiveIds.has(user.userAccountId)) continue;
      if (
        !matchesSearch(
          user.firstName,
          user.lastName,
          user.jobTitle,
          user.emailAddress
        )
      )
        continue;
      results.push({
        userAccountId: user.userAccountId,
        firstName: user.firstName,
        lastName: user.lastName,
        jobTitle: user.jobTitle,
      });
    }
    return results;
  }, [usersById, recipientIds, addedWontReceiveIds, matchesSearch, search]);

  /* ─── Actions ─── */

  const handleRemove = useCallback(
    (userAccountId: number) => {
      if (!settings) return;

      const includedIds = settings.includedUsers
        .map((u) => u.userAccountId)
        .filter((id) => id !== userAccountId);

      updateSettings({
        mode: settings.mode ?? "all_who_qualify",
        thresholdPercentage: settings.thresholdPercentage,
        excludedUserAccountIds: settings.excludedUsers.map(
          (u) => u.userAccountId
        ),
        includedUserAccountIds: includedIds,
      });

      const recipient = preview?.recipients.find(
        (r) => r.userAccountId === userAccountId
      );
      const user = recipient ?? usersById?.[userAccountId];
      const name = user ? `${user.firstName} ${user.lastName}` : "User";
      toast.success(`${name} removed`, successNotificationOptions);
    },
    [settings, preview, usersById, updateSettings]
  );

  const handleCloseDrawer = useCallback(() => {
    setSearch("");
    onClose();
  }, [onClose]);

  const handleExclude = useCallback(
    (userAccountId: number) => {
      if (!settings) return;

      const excludedIds = [
        ...settings.excludedUsers.map((u) => u.userAccountId),
        userAccountId,
      ];

      updateSettings({
        mode: settings.mode ?? "all_who_qualify",
        thresholdPercentage: settings.thresholdPercentage,
        excludedUserAccountIds: excludedIds,
        includedUserAccountIds: settings.includedUsers.map(
          (u) => u.userAccountId
        ),
      });

      const user = preview?.recipients.find(
        (r) => r.userAccountId === userAccountId
      );
      const name = user ? `${user.firstName} ${user.lastName}` : "User";
      toast.success(`${name} excluded`, successNotificationOptions);
    },
    [settings, preview, updateSettings]
  );

  // Note: handleInclude is only wired to the "re_include" action type, which is
  // only rendered in the manuallyExcluded section — a section that only appears
  // in all_who_qualify mode. The guard below is intentional; this handler should
  // never be called in specific_people mode.
  const handleInclude = useCallback(
    (userAccountId: number) => {
      if (!settings) return;

      if (currentMode === "all_who_qualify") {
        const isExcluded = settings.excludedUsers.some(
          (u) => u.userAccountId === userAccountId
        );

        const excludedIds = isExcluded
          ? settings.excludedUsers
              .map((u) => u.userAccountId)
              .filter((id) => id !== userAccountId)
          : settings.excludedUsers.map((u) => u.userAccountId);

        const includedIds = isExcluded
          ? settings.includedUsers.map((u) => u.userAccountId)
          : [
              ...settings.includedUsers.map((u) => u.userAccountId),
              userAccountId,
            ];

        updateSettings({
          mode: settings.mode ?? "all_who_qualify",
          thresholdPercentage: settings.thresholdPercentage,
          excludedUserAccountIds: excludedIds,
          includedUserAccountIds: includedIds,
        });

        const user = preview?.recipients.find(
          (r) => r.userAccountId === userAccountId
        );
        const name = user ? `${user.firstName} ${user.lastName}` : "User";
        toast.success(`${name} re-included`, successNotificationOptions);
      }
    },
    [settings, currentMode, preview, updateSettings]
  );

  const handleAdd = useCallback(
    (userAccountId: number) => {
      if (
        !settings ||
        settings.includedUsers.some((u) => u.userAccountId === userAccountId)
      ) {
        return;
      }

      const includedIds = [
        ...settings.includedUsers.map((u) => u.userAccountId),
        userAccountId,
      ];

      updateSettings({
        mode: settings.mode ?? "all_who_qualify",
        thresholdPercentage: settings.thresholdPercentage,
        excludedUserAccountIds: settings.excludedUsers.map(
          (u) => u.userAccountId
        ),
        includedUserAccountIds: includedIds,
      });

      const user = usersById?.[userAccountId];
      const name = user ? `${user.firstName} ${user.lastName}` : "User";
      toast.success(`${name} added`, successNotificationOptions);
    },
    [settings, usersById, updateSettings]
  );

  const focusSearch = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  /* ─── Jump pills data ─── */

  const jumpPills = useMemo(() => {
    if (!sections) return [];

    const pills: {
      label: string;
      count: number;
      dotClass: string;
      targetId: string;
      dimmed?: boolean;
    }[] = [];

    if (sections.mode === "specific_people") {
      pills.push({
        label: "Receiving",
        count: sections.manuallyAdded.length,
        dotClass: "bg-green-500",
        targetId: "section-receiving",
      });
    } else {
      const receivingCount =
        sections.manuallyAdded.length + sections.eligibleByRules.length;
      pills.push(
        {
          label: "Receiving",
          count: receivingCount,
          dotClass: "bg-green-500",
          targetId: "section-receiving",
        },
        {
          label: "Excluded",
          count: sections.manuallyExcluded.length,
          dotClass: "bg-red-500",
          targetId: "section-excluded",
          dimmed: sections.manuallyExcluded.length === 0,
        },
        {
          label: "Excluded due to threshold",
          count: sections.belowThreshold.length,
          dotClass: "bg-charcoal-40",
          targetId: "section-noteligible",
        }
      );
    }

    if (seniorManagerRecipients.length > 0) {
      pills.push({
        label: "Receiving Senior Manager Check-in Report",
        count: seniorManagerRecipients.length,
        dotClass: "bg-sapphire-60",
        targetId: "section-senior-manager",
      });
    }

    return pills;
  }, [sections, seniorManagerRecipients]);

  /* ─── Loading skeleton ─── */

  const getLoadingContent = () => (
    <div className="px-6 py-3 flex flex-col gap-3">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-8 w-full" />
      <div className="flex flex-col gap-2 mt-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    </div>
  );

  /* ─── Render recipient rows ─── */

  const renderRecipientRows = (
    items: T[],
    actionType: ActionType,
    onAction: (userAccountId: number) => void
  ) => {
    const visible = items.slice(0, SECTION_DISPLAY_LIMIT);
    return visible.map((r) => (
      <RecipientRow
        key={r.userAccountId}
        recipient={r}
        onAction={onAction}
        isSaving={isSaving}
        actionType={actionType}
        renderSubtitle={renderRecipientSubtitle}
      />
    ));
  };

  /* ─── Body content ─── */

  const getBodyContent = () => {
    if (!preview || !sections) {
      return (
        <div className="px-6 py-8 text-center text-sm text-charcoal-40">
          No preview data available.
        </div>
      );
    }

    const sectionArrays =
      sections.mode === "specific_people"
        ? [sections.manuallyAdded, sections.qualified]
        : [
            sections.manuallyAdded,
            sections.eligibleByRules,
            sections.manuallyExcluded,
            sections.belowThreshold,
          ];

    const hasNoResults =
      search.length > 0 &&
      sectionArrays.every((arr) => arr.length === 0) &&
      seniorManagerRecipients.length === 0 &&
      addedWontReceive.length === 0 &&
      nonRecipientUsers.length === 0;

    const noop = () => {};

    const seniorManagerSection = seniorManagerRecipients.length > 0 && (
      <div id="section-senior-manager">
        <SectionHeader
          title="Receiving Senior Manager Check-in Report"
          count={seniorManagerRecipients.length}
          titleClassName="text-sapphire-100"
          tooltip="These people already receive the Senior Manager Check-in Report, which includes Team Leader data. They cannot be removed from this report."
        />
        {renderRecipientRows(seniorManagerRecipients, "info", noop)}
      </div>
    );
    const nonRoleLabel = `Non ${recipientRoleLabel}`;

    const nonRoleUsersSection = search.length > 0 &&
      nonRecipientUsers.length > 0 && (
        <div>
          <SectionHeader
            title={`Non ${recipientRoleLabel}s`}
            count={nonRecipientUsers.length}
          />
          {nonRecipientUsers.slice(0, SECTION_DISPLAY_LIMIT).map((user) => (
            <NonRecipientRow
              key={user.userAccountId}
              user={user}
              isSaving={isSaving}
              label={nonRoleLabel}
            />
          ))}
        </div>
      );

    return (
      <>
        {sections.mode === "specific_people" ? (
          <>
            {/* Receiving */}
            {sections.manuallyAdded.length > 0 && (
              <div id="section-receiving">
                <SectionHeader
                  title="Receiving"
                  count={sections.manuallyAdded.length}
                  titleClassName="text-green-600"
                  icon={faCheck}
                  id="receiving-header"
                />
                {renderRecipientRows(
                  sections.manuallyAdded,
                  "remove",
                  handleRemove
                )}
              </div>
            )}

            {seniorManagerSection}

            {/* Other people (when searching) */}
            {search.length > 0 && sections.qualified.length > 0 && (
              <div>
                <SectionHeader
                  title={`${recipientRoleLabel}s`}
                  count={sections.qualified.length}
                />
                {renderRecipientRows(sections.qualified, "add", handleAdd)}
              </div>
            )}
            {nonRoleUsersSection}

            {/* Add more hint */}
            {sections.manuallyAdded.length > 0 && !search && (
              <>
                <Separator className="mt-2 border-dashed" />
                <div className="px-6 py-3 text-center">
                  <ShadcnButton
                    variant="tertiary"
                    size="sm"
                    onClick={focusSearch}
                    className="gap-1.5"
                  >
                    <FontAwesomeIcon icon={faPlus} className="text-[11px]" />
                    Add more people
                  </ShadcnButton>
                </div>
              </>
            )}

            {/* Empty */}
            {sections.manuallyAdded.length === 0 &&
              addedWontReceive.length === 0 &&
              !search && (
                <div className="px-6 py-12 text-center text-charcoal-80 text-[13px]">
                  <div className="font-medium text-charcoal-130 mb-1">
                    No one added yet
                  </div>

                  <ShadcnButton
                    variant="tertiary"
                    size="sm"
                    onClick={focusSearch}
                    className="gap-1.5"
                  >
                    <FontAwesomeIcon icon={faPlus} className="text-[11px]" />
                    Search above to add {recipientRoleLabel.toLowerCase()}s to
                    this report.
                  </ShadcnButton>
                </div>
              )}
          </>
        ) : (
          <>
            {/* ─── All Who Qualify Mode ─── */}

            {/* Receiving (parent section) */}
            {(sections.manuallyAdded.length > 0 ||
              sections.eligibleByRules.length > 0) && (
              <div id="section-receiving">
                <SectionHeader
                  title="Receiving"
                  count={
                    sections.manuallyAdded.length +
                    sections.eligibleByRules.length
                  }
                  titleClassName="text-green-600"
                  icon={faCheck}
                  id="receiving-header"
                />

                {sections.manuallyAdded.length > 0 && (
                  <>
                    <SubSectionHeader
                      title="Manually added"
                      count={sections.manuallyAdded.length}
                      tooltip={`People who have been manually added. They'll receive a report regardless of the rule, as long as at least one ${coverageSubject.member} completes a check-in.`}
                    />
                    {renderRecipientRows(
                      sections.manuallyAdded,
                      "remove_manual_add",
                      handleRemove
                    )}
                  </>
                )}

                {sections.eligibleByRules.length > 0 && (
                  <>
                    <SubSectionHeader
                      title="Based on rules"
                      count={sections.eligibleByRules.length}
                      tooltip={`People where at least ${
                        currentThreshold ?? 50
                      }% of ${
                        coverageSubject.group
                      } are enrolled in check-ins, so they meet the current rule.`}
                    />
                    {renderRecipientRows(
                      sections.eligibleByRules,
                      "exclude",
                      handleExclude
                    )}
                  </>
                )}
              </div>
            )}

            {seniorManagerSection}

            {/* Excluded */}
            {sections.manuallyExcluded.length > 0 && (
              <div id="section-excluded">
                <SectionHeader
                  title="Manually excluded"
                  count={sections.manuallyExcluded.length}
                  titleClassName="text-red-500"
                  icon={faXmark}
                  tooltip="People who would qualify by the rule but have been manually excluded. They won't receive a report until they're re-included."
                />
                {renderRecipientRows(
                  sections.manuallyExcluded,
                  "re_include",
                  handleInclude
                )}
              </div>
            )}

            {/* Not eligible */}
            {sections.belowThreshold.length > 0 && (
              <div id="section-noteligible">
                <SectionHeader
                  title="Excluded due to threshold"
                  count={sections.belowThreshold.length}
                  countSuffix={`${
                    sections.belowThreshold.length
                  } people · showing ${Math.min(
                    4,
                    sections.belowThreshold.length
                  )}`}
                  tooltip={`Fewer than ${currentThreshold ?? 50}% of ${
                    coverageSubject.group
                  } are enrolled in check-ins, so they don't meet the current rule. They won't receive a report unless you lower the threshold or add them manually.`}
                />
                {sections.belowThreshold.slice(0, 4).map((r) => (
                  <RecipientRow
                    key={r.userAccountId}
                    recipient={r}
                    onAction={handleInclude}
                    isSaving={isSaving}
                    actionType="include"
                    renderSubtitle={renderRecipientSubtitle}
                  />
                ))}
                {sections.belowThreshold.length > 4 && (
                  <div className="px-6 py-3 text-center text-[11px] text-charcoal-60">
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="mr-1 text-charcoal-40"
                    />
                    Use search above to find any of the{" "}
                    {sections.belowThreshold.length} not-eligible people.
                  </div>
                )}
              </div>
            )}

            {nonRoleUsersSection}

            {/* Empty */}
            {sections.manuallyAdded.length === 0 &&
              sections.eligibleByRules.length === 0 &&
              addedWontReceive.length === 0 &&
              !search && (
                <div className="px-6 py-12 text-center text-charcoal-80 text-[13px]">
                  <div className="font-medium text-charcoal-130 mb-1">
                    No one currently receiving
                  </div>
                  <div>
                    Lower the threshold, or add someone manually using
                    &quot;Find someone&quot;.
                  </div>
                </div>
              )}

            {/* Add more hint */}
            {(sections.manuallyAdded.length > 0 ||
              sections.eligibleByRules.length > 0) &&
              !search && (
                <>
                  <Separator className="mt-2 border-dashed" />
                  <div className="px-6 py-3 text-center">
                    <ShadcnButton
                      variant="tertiary"
                      size="sm"
                      onClick={focusSearch}
                      className="gap-1.5"
                    >
                      <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className="text-[11px]"
                      />
                      Find someone to add, exclude, or re-include
                    </ShadcnButton>
                  </div>
                </>
              )}
          </>
        )}

        {/* No search results */}
        {hasNoResults && (
          <div className="py-6 text-center text-xs text-charcoal-40">
            No results matching &quot;{search}&quot;
          </div>
        )}
      </>
    );
  };

  return (
    <Drawer direction="right" open={isOpen} onOpenChange={handleCloseDrawer}>
      <DrawerContent
        overlayClassName="bg-black/20"
        className={`right-0 top-0 left-auto bottom-auto mt-0 h-full ${DRAWER_WIDTH} rounded-l-[10px] rounded-t-none figtree-container`}
      >
        {/* Header */}
        <DrawerHeader className="justify-between items-center flex-shrink-0 pb-0 text-left block">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DrawerTitle className="text-[18px]">
                {reportName} — Next Monday
              </DrawerTitle>
              <p className="text-[13px] text-charcoal-80 mt-1">
                Preview and adjust who receives next Monday&apos;s {reportName}.
              </p>
            </div>
            <DrawerClose asChild>
              <Button variant="secondary-blue">
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </DrawerClose>
          </div>

          {/* Context strip */}
          <Card className="mt-3 shadow-none">
            <CardContent className="p-3 flex items-center gap-3 text-[13px] flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="text-charcoal-80">Mode:</span>
                <span className="text-charcoal-130 font-medium">
                  {modeLabel}
                </span>
              </div>
              {currentMode === "all_who_qualify" && currentThreshold && (
                <>
                  <Separator orientation="vertical" className="h-3.5" />
                  <div className="flex items-center gap-1.5">
                    <span className="text-charcoal-80">Threshold:</span>
                    <span className="text-charcoal-130 font-medium">
                      {currentThreshold}%
                    </span>
                  </div>
                </>
              )}
              {onEditSettings && (
                <ShadcnButton
                  variant="tertiary"
                  size="sm"
                  className="ml-auto"
                  onClick={onEditSettings}
                >
                  Edit settings
                </ShadcnButton>
              )}
            </CardContent>
          </Card>
        </DrawerHeader>

        {/* Search */}
        <div className="px-6 pt-3 pb-0 flex-shrink-0 bg-white">
          <div className="relative flex-1 min-w-0">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-60 text-sm pointer-events-none z-10"
            />
            <Input
              ref={searchInputRef}
              className="h-[38px] pl-[30px] text-sm border-charcoal-20 focus-visible:ring-1 focus-visible:ring-sapphire-60 focus-visible:ring-offset-0 focus-visible:border-sapphire-100"
              placeholder="Search for people to include/exclude..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>

        {/* Jump pills */}
        {!isLoading && jumpPills.length > 0 && (
          <>
            <JumpPills pills={jumpPills} />
            <Separator />
          </>
        )}

        {/* Body */}
        <ScrollArea className="flex-1">
          <div className="pb-4">
            {isLoading ? getLoadingContent() : getBodyContent()}
          </div>
        </ScrollArea>

        {/* Footer */}
        <Separator />
        <div className="px-6 py-3 bg-charcoal-02 text-xs text-charcoal-80 flex-shrink-0 flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-green-500 font-medium">
            <FontAwesomeIcon icon={faCheck} className="text-[11px]" />
            Auto-saved
          </span>
          <span>Based on check-in data through this Thursday.</span>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
